import { ReactNode, useLayoutEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { MenuCartItem } from '../../models/menu.types';
import { ProductTile } from '../product-tile/ProductTile';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import styles from './MenuPage.module.scss';
import { checkKeywordMatch } from '../../constants/searchKeywords';

type Props = {
  menuItemArr: MenuCartItem[];
  isSkipped: boolean;
  animateTiles: boolean;
  isActive: boolean;
  isLoggedIn?: boolean;
  leftAligned?: boolean;
  header?: ReactNode;
  searchQuery?: string;
  onResultCount?: (count: number) => void;
  onAddToCart: (menuItem: MenuCartItem) => void;
  onUpdateCartQuantity: (cartItem: MenuCartItem, quantity: number) => void;
  handleSoldOutLtoClick: () => void;
  'data-heap-merch-group-title'?: string;
};

export default function ProductCards({
  menuItemArr,
  isSkipped,
  animateTiles,
  isActive,
  isLoggedIn,
  leftAligned,
  header,
  searchQuery,
  onResultCount,
  onAddToCart,
  onUpdateCartQuantity,
  handleSoldOutLtoClick,
  'data-heap-merch-group-title': merchGroupTitle,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { menu } = useMenuWithCart();
  const itemMerchGroupTitle = useMemo(() => {
    const map = new Map<number, string>();
    menu?.merchandisingGroups?.forEach((group) => {
      group.merchandisingGroupMenuItems.forEach((mgmi) => {
        map.set(mgmi.menuItemId, group.title);
      });
    });
    return map;
  }, [menu?.merchandisingGroups]);

  const {
    keywordFilteredItems,
    hasKeywordMatch,
  } = useMemo(() => {
    const matchedMappings = checkKeywordMatch(searchQuery ?? '');
    if (matchedMappings.length === 0) {
      return {
        keywordFilteredItems:
        menuItemArr,
        hasKeywordMatch: false,
      };
    }
    return {
      keywordFilteredItems: menuItemArr.filter((item) => matchedMappings.some((m) => {
        const values = Array.isArray(m.value) ? m.value : [m.value];
        if (m.property === 'productType') return values.includes(item.productType);
        return item.filterTags?.some((tag) => values.includes(tag));
      })),
      hasKeywordMatch: true,
    };
  }, [menuItemArr, searchQuery]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const q = searchQuery?.toLowerCase() ?? '';
    const tiles = containerRef.current.querySelectorAll<HTMLElement>('[data-menu-item-id]');
    let visibleCount = 0;
    tiles.forEach((tile) => {
      const matches = hasKeywordMatch || (() => {
        const text = (tile.textContent ?? '').toLowerCase();
        const terms = q.split(/\s+/)
          .filter(Boolean);
        return !q || terms.some((term) => text.search(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) !== -1);
      })();
      tile.style.display = matches ? '' : 'none';
      if (matches) visibleCount += 1;
    });
    onResultCount?.(visibleCount);
  }, [searchQuery, keywordFilteredItems]);

  return (
    <div
      ref={containerRef}
      className={`${styles.product_tiles_display} ${leftAligned ? styles.left_aligned : ''} ${
        isSkipped ? styles.is_skipped : ''
      }`}
    >
      {header && (
        <div className={styles.product_tiles_header}>{header}</div>
      )}
      {(() => {
        // When a keyword mapping matched (e.g. "low salt" → Low Sodium tag), show those
        // only tagged items. Otherwise, rank by relevance:
        // Score 2: every search term in the title (strongest match)
        // Score 1: at least one search term in the title (partial match)
        // Score 0: match only in hidden metadata (description, tags, ingredients, etc.)
        if (!searchQuery || hasKeywordMatch) return keywordFilteredItems;
        const terms = searchQuery.toLowerCase()
          .split(/\s+/)
          .filter(Boolean);
        const score = (item: MenuCartItem) => {
          const title = item.title.toLowerCase();
          if (terms.every((t) => title.includes(t))) return 2;
          if (terms.some((t) => title.includes(t))) return 1;
          return 0;
        };
        return [...keywordFilteredItems].sort((a, b) => score(b) - score(a));
      })()
        .map((menuItem: MenuCartItem, index: number) => {
          const quantityInCart = menuItem.quantity || 0;
          const showSoldOutOverlay = !menuItem.isInStock && quantityInCart === 0;
          return (
            <motion.div
              key={menuItem.id}
              data-menu-item-id={menuItem.id}
              layout
              initial={animateTiles ? {
                x: 40,
                opacity: 0,
              } : undefined}
              animate={{
                x: 0,
                opacity: 1,
              }}
              exit={animateTiles ? {
                x: -40,
                opacity: 0,
              } : undefined}
              transition={{
                duration: 0.25,
                ease: 'easeOut',
              }}
            >
              <ProductTile
                title={menuItem.title}
                subTitle={menuItem.subtitle}
                imageUrl={menuItem.imageURL}
                totalPrice={menuItem.skuPrice}
                isSaleTagEnabled={menuItem.saleTagEnabled}
                salePrice={menuItem.salePrice}
                pricePerServing={menuItem.pricePerServing}
                salePricePerServing={menuItem.salePricePerServing}
                servingSize={menuItem.servingSize}
                quantity={menuItem.quantity || 0}
                isInStock={menuItem.isInStock}
                isActive={isActive}
                badgeTags={menuItem.badgeTags}
                cookTime={menuItem.cookTime}
                brandName={menuItem.brandName}
                unitSize={menuItem.unitSize}
                path={menuItem.productPath}
                onAddToCart={() => onAddToCart(menuItem)}
                onUpdateCartQuantity={
                  (quantity: number) => onUpdateCartQuantity(menuItem, quantity)
                }
                productType={menuItem.productType}
                skuId={menuItem.skuId}
                isLtoBox={menuItem.isLtoBox}
                ltoServings={menuItem.ltoServings}
                description={menuItem.description}
                handleSoldOutLtoClick={handleSoldOutLtoClick}
                soldOut={showSoldOutOverlay}
                productId={menuItem.productId}
                isFavorite={menuItem.isFavorite}
                isLoggedIn={isLoggedIn}
                lazyLoadImage={index > 8}
                data-heap-merch-group-title={merchGroupTitle}
              />
              <span aria-hidden="true" style={{ display: 'none' }}>
                {[
                  menuItem.description,
                  menuItem.filterTags?.join(' '),
                  menuItem.productType,
                  menuItem.ingredients?.join(' '),
                  menuItem.tags?.map((t) => t.name)
                    .join(' '),
                  itemMerchGroupTitle.get(menuItem.id) ?? merchGroupTitle,
                ].filter(Boolean)
                  .join(' ')}
              </span>
            </motion.div>
          );
        })}
    </div>
  );
}
